/**
 * Typed fetch wrapper.
 * - Attaches X-Platform-Token header automatically.
 * - Attaches Authorization: Bearer <accessToken> when available.
 * - On 401, attempts a single token refresh then retries.
 * - Throws TryStyleApiError on non-2xx responses.
 */

import { storage, STORAGE_KEYS } from './storage';

export class TryStyleApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'TryStyleApiError';
  }
}

export interface FetcherOptions {
  /** Base API URL, e.g. https://adminbe.trystyle.fashion */
  apiUrl: string;
  /** Returns the current platform token or null (can be async — auto-refreshes if expired) */
  getPlatformToken: () => string | null | Promise<string | null>;
  /** Returns the current user access token or null */
  getAccessToken: () => string | null;
  /** Called when a 401 occurs and a refresh is attempted */
  onRefreshToken: () => Promise<string | null>;
  /** Refresh the platform token (re-PKCE / re-clientId) */
  onRefreshPlatformToken: () => Promise<string | null>;
  /** Called when refresh also fails (user must re-login) */
  onUnauthenticated: () => void;
  /** Returns device context headers (X-Device-ID, X-Device-Fingerprint, etc.) */
  getDeviceHeaders?: () => Record<string, string> | Promise<Record<string, string>>;
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestConfig {
  method?: HttpMethod;
  body?: unknown;
  /** Use FormData instead of JSON */
  formData?: FormData;
  /** Extra headers */
  headers?: Record<string, string>;
  /** Skip token refresh retry (used internally) */
  _retry?: boolean;
}

function getBrowserDeviceHeaders(): Record<string, string> {
  if (typeof globalThis.window === 'undefined' || !globalThis.crypto?.randomUUID) {
    return {};
  }
  let deviceId = storage.get(STORAGE_KEYS.DEVICE_ID);
  let fingerprint = storage.get(STORAGE_KEYS.DEVICE_FINGERPRINT);
  if (!deviceId) {
    deviceId = globalThis.crypto.randomUUID();
    storage.set(STORAGE_KEYS.DEVICE_ID, deviceId);
  }
  if (!fingerprint) {
    fingerprint = globalThis.crypto.randomUUID();
    storage.set(STORAGE_KEYS.DEVICE_FINGERPRINT, fingerprint);
  }
  return {
    'X-Device-ID': deviceId,
    'X-Device-Fingerprint': fingerprint,
    'X-Platform': 'web',
  };
}

export function createFetcher(opts: FetcherOptions) {
  async function rawFetch(path: string, config: RequestConfig = {}): Promise<Response> {
    const { method = 'GET', body, formData, headers: extraHeaders = {}, _retry = false } = config;

    const platformToken = await opts.getPlatformToken();
    const accessToken = opts.getAccessToken();

    const headers: Record<string, string> = {
      ...getBrowserDeviceHeaders(),
      ...extraHeaders,
    };

    if (opts.getDeviceHeaders) {
      const deviceHeaders = await opts.getDeviceHeaders();
      Object.assign(headers, deviceHeaders);
    }

    if (platformToken) {
      headers['X-Platform-Token'] = platformToken;
    }
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    let fetchBody: BodyInit | undefined;
    if (formData) {
      fetchBody = formData;
    } else if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
      fetchBody = JSON.stringify(body);
    }

    const res = await fetch(`${opts.apiUrl}${path}`, {
      method,
      headers,
      body: fetchBody,
      credentials: 'omit',
    });

    if (res.status === 401 && !_retry) {
      const newAccessToken = await opts.onRefreshToken();
      if (newAccessToken) {
        try {
          return await rawFetch(path, { ...config, _retry: true });
        } catch (err) {
          opts.onUnauthenticated();
          throw err;
        }
      }

      try {
        const newPlatformToken = await opts.onRefreshPlatformToken();
        if (newPlatformToken) {
          try {
            return await rawFetch(path, { ...config, _retry: true });
          } catch (err) {
            opts.onUnauthenticated();
            throw err;
          }
        }
      } catch { /* platform refresh failed */ }

      opts.onUnauthenticated();
      throw new TryStyleApiError(401, 'UNAUTHORIZED', 'Session expired. Please log in again.');
    }

    if (!res.ok) {
      let message = `Request failed with status ${res.status}`;
      try {
        const errBody = await res.json() as { message?: string };
        if (errBody.message) message = errBody.message;
      } catch { /* ignore */ }
      throw new TryStyleApiError(res.status, `HTTP_${res.status}`, message);
    }

    return res;
  }

  async function request<T>(path: string, config: RequestConfig = {}): Promise<T> {
    const res = await rawFetch(path, config);
    return res.json() as Promise<T>;
  }

  async function requestBlob(
    path: string,
    config: Omit<RequestConfig, 'body' | 'formData'> = {},
  ): Promise<Blob> {
    const res = await rawFetch(path, config);
    return res.blob();
  }

  return { request, requestBlob };
}

export type Fetcher = ReturnType<typeof createFetcher>;
